import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAssignments } from '../services/api';
import logo from '../images/image.png';
import { useI18n } from '../context/I18nContext';

const ClassroomPageStudent = () => {
  const { classroomId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeOffsetMs, setTimeOffsetMs] = useState(0);

  useEffect(() => {
    fetchAssignments();
  }, [classroomId]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await getAssignments(classroomId);
      setAssignments(response.data);
      const serverTime = response.headers['x-server-time'];
      if (serverTime) {
        setTimeOffsetMs(Number(serverTime) - Date.now());
      }
    } catch (err) {
      console.error('Failed to load assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDateTime = (value) => {
    if (!value) {
      return t('common.noDeadline');
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? t('common.noDeadline') : date.toLocaleString();
  };

  const getTimeLeft = (value) => {
    if (!value) {
      return t('common.noDeadline');
    }
    const due = new Date(value).getTime();
    if (Number.isNaN(due)) {
      return t('common.noDeadline');
    }
    const diff = due - (Date.now() + timeOffsetMs);
    if (diff <= 0) {
      return t('common.pastDue');
    }
    const minutes = Math.floor(diff / 60000);
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;
    if (days > 0) {
      return `${days}d ${hours}h left`;
    }
    if (hours > 0) {
      return `${hours}h ${mins}m left`;
    }
    return `${mins}m left`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/student-dashboard')}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              ← {t('common.back')}
            </button>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-700">
                Sa7a7ly
              </p>
              <h1 className="text-2xl font-bold text-slate-900">
                {t('common.classroom')}
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600">
              {user?.name}
            </span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition"
            >
              {t('common.logout')}
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-6 shadow-sm">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-emerald-200/40 blur-2xl" />
          <div className="absolute -left-12 -bottom-12 h-44 w-44 rounded-full bg-sky-200/40 blur-2xl" />
          <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <img
                  src={logo}
                  alt="Sa7a7ly logo"
                  className="h-12 w-12 rounded-xl bg-white p-2 shadow"
                />
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
                    {t('classroom.classroomHub')}
                  </p>
                  <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">
                    {t('classroom.stayOnTop')}
                  </h2>
                </div>
              </div>
              <p className="mt-4 text-base leading-relaxed text-slate-700">
                {t('classroom.studentHubBody')}
              </p>
            </div>
            <div className="grid w-full max-w-sm grid-cols-2 gap-4 text-center">
              <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
                <p className="text-2xl font-bold text-slate-900">
                  {assignments.length}
                </p>
                <p className="text-sm text-slate-600">{t('common.assignments')}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
                <p className="text-2xl font-bold text-slate-900">{t('classroom.submit')}</p>
                <p className="text-sm text-slate-600">{t('classroom.inOneClick')}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
                <p className="text-2xl font-bold text-slate-900">{t('classroom.track')}</p>
                <p className="text-sm text-slate-600">{t('classroom.yourPoints')}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
                <p className="text-2xl font-bold text-slate-900">{t('classroom.clear')}</p>
                <p className="text-sm text-slate-600">{t('classroom.nextSteps')}</p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <p className="text-slate-600">{t('common.loading')}</p>
          </div>
        ) : assignments.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 shadow-sm p-10 text-center">
            <p className="text-slate-600">{t('classroom.noAssignments')}</p>
            <p className="mt-2 text-sm text-slate-500">
              {t('classroom.checkBackLater')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-2xl font-bold text-slate-900">
                {t('common.assignments')}
              </h2>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600">
                {assignments.length} {t('common.total')}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {assignments.map((assignment) => (
                <div
                  key={assignment._id}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-xl font-bold text-slate-900">
                      {assignment.title}
                    </h3>
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                    {assignment.totalPoints} {t('common.points')}
                  </span>
                  </div>
                  <p className="mt-2 text-slate-600">
                    {assignment.description}
                  </p>
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    <p>{t('common.due')}: {formatDateTime(assignment.dueDate)}</p>
                    <p>{t('common.timeLeft')}: {getTimeLeft(assignment.dueDate)}</p>
                  </div>
                  <button
                    onClick={() =>
                      navigate(`/submit-assignment/${assignment._id}`)
                    }
                    className="mt-5 w-full rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white transition hover:bg-emerald-700"
                  >
                    {t('classroom.submitAssignment')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassroomPageStudent;
